import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    TextField,
    Button,
    Stack,
    Paper,
    Divider,
    Typography,
    Card,
    CardContent,
    IconButton,
} from '@mui/material';
import { request } from '../../Utils/request';
import MediaUpload from '../../Components/MediaUpload';
import { extractHashtags } from '../../services/CommonFunctions';
import CategoryChips from '../../Components/CategoryChips';
import MapSelector from '../../Components/MapSelector';
import { getSignedUploadUrls, uploadFileToSignedUrl } from '../../services/API/Api';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducers';
import { AppDispatch } from '../../store';
import { fetchCategories } from '../../services/CommonApiCall';
import { fileToBase64, base64ToFile } from '../../Utils/base64Converter';
import { placeDraftUtils } from '../../Utils/placeDraftStorage';
import { ReactComponent as DraftIcon } from "../../Icons/svg/draft.svg";
import TopNavBar from '../../Components/TopNavigation/TopNavBar';


interface PlaceFormData {
    name: string;
    location: {
        lat: number | string;
        lng: number | string;
    };
    files: Array<{
        url: string;
        fileType: string;
        featured: boolean;
    }>;
    description: string;
    categories: string[];
    tags: string[];
    address: string
    social: { key: string; value: string }[];
    owner_details: {
        name: string;
        email: string;
        phone: string;
        whatsapp: string;
    };
    // type: string;
}





const PlaceForm: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const emptyForm = {
        name: '',
        location: {
            lat: '',
            lng: '',
        },
        files: [],
        description: '',
        categories: [],
        tags: [],
        address: '',
        social: [
            { key: 'website', value: '' },
            { key: 'facebook', value: '' },
            { key: 'instagram', value: '' },
            { key: 'youtube', value: '' }
        ],
        owner_details: {
            name: '',
            email: '',
            phone: '',
            whatsapp: ''
        },
        // type: '',
    };
    const navigate = useNavigate();
    const [form, setForm] = useState<PlaceFormData>(emptyForm);
    // const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [imagesData, setImagesData] = useState<any[]>([])
    const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [currentLocation, setCurrentLocation] = useState<{ lat: number, lng: number, address: string } | null>(null);
    const categories = useSelector((state: RootState) => state.category.categories);
    const placeDrafts = useSelector((state: RootState) => state.placeDraft?.drafts || []);

    useEffect(() => {
        fetchCategories(dispatch, 'post-places');
    }, []);


    // Handle draft loading from navigation state
    useEffect(() => {
        const location = window.location;
        const urlParams = new URLSearchParams(location.search);
        const draftId = urlParams.get('draftId');
        const isPreview = urlParams.get('preview') === 'true';

        if (draftId) {
            handleLoadDraft(draftId);
            if (isPreview) {
                // Handle preview mode if needed
                console.log('Preview mode for draft:', draftId);
            }
        }
    }, []);


    const handleDescriptionBlur = () => {
        const hashtags = extractHashtags(form.description);
        if (hashtags.length > 0) {
            setForm(prev => ({
                ...prev,
                tags: Array.from(new Set([...prev.tags, ...hashtags]))
            }));
        }
    };

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };



    const handleLocationSelect = (lat: number, lng: number, address: string) => {
        setForm(prev => ({
            ...prev,
            location: {
                lat: lat,
                lng: lng,
            }
        }));
        setCurrentLocation({ lat, lng, address });
    };

    // Draft handling functions
    const handleSaveDraft = async () => {
        try {
            setIsSavingDraft(true);

            // Convert File objects to base64 when saving
            const serializedImagesData = await Promise.all(imagesData.map(async img => ({
                url: img.url,
                caption: img.caption || '',
                featured: img.featured || false,
                fileBase64: img.file ? await fileToBase64(img.file) : null,
                fileName: img.file?.name || '',
                fileType: img.file?.type || '',
                fileSize: img.file?.size || 0
            })));

            const draftData = {
                name: form.name,
                location: form.location,
                address: form.address,
                description: form.description,
                categories: form.categories,
                tags: form.tags,
                social: form.social,
                owner_details: form.owner_details,
                imagesData: serializedImagesData,
            };

            if (currentDraftId) {
                placeDraftUtils.updateDraft(dispatch, currentDraftId, draftData);
                alert('Draft updated successfully!');
            } else {
                placeDraftUtils.saveDraft(dispatch, draftData);
                setCurrentDraftId(Date.now().toString());
                alert('Draft saved successfully!');
            }
        } catch (error) {
            console.error('Error saving draft:', error);
            alert('Failed to save draft. Please try again.');
        } finally {
            setIsSavingDraft(false);
        }
    };

    const handleLoadDraft = (draftId: string) => {
        const draft = placeDrafts.find(d => d.id === draftId);
        if (draft) {
            setCurrentDraftId(draftId);
            setForm({
                name: draft.name,
                location: draft.location,
                address: draft.address,
                description: draft.description,
                categories: draft.categories,
                tags: draft.tags,
                social: draft.social,
                owner_details: draft.owner_details,
                files: [],
            });
            setSelectedCategories(draft.categories);

            // Set current location for map display if location data exists
            if (draft.location.lat && draft.location.lng && draft.address) {
                setCurrentLocation({
                    lat: typeof draft.location.lat === 'string' ? parseFloat(draft.location.lat) : draft.location.lat,
                    lng: typeof draft.location.lng === 'string' ? parseFloat(draft.location.lng) : draft.location.lng,
                    address: draft.address
                });
            }

            // Convert base64 back to File objects when loading
            const reconstructedImagesData = (draft.imagesData || []).map((img: any) => {
                const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
                return {
                    url: newFile ? URL.createObjectURL(newFile) : img.url,
                    caption: img.caption,
                    featured: img.featured,
                    file: newFile
                };
            });

            setImagesData(reconstructedImagesData);
        }
    };

    const handleDeleteDraft = (draftId: string) => {
        placeDraftUtils.deleteDraft(dispatch, draftId);
        if (currentDraftId === draftId) {
            setCurrentDraftId(null);
            setForm(emptyForm);
            setImagesData([]);
            setSelectedCategories([]);
            setCurrentLocation(null);
        }
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        console.log('handleSubmit', form);

        const mediaFiles = imagesData;

        if (!mediaFiles || mediaFiles.length === 0) {
            console.error('No media files selected');
            return;
        }

        try {
            // Step 1: Prepare filesProp for all media
            const filesProp = mediaFiles.map((item: any) => ({
                folder: 'place',
                fileType: item.file.type,
            }));

            // Step 2: Get signed upload URLs for all files
            const signedUrlsData = await getSignedUploadUrls(filesProp);
            const signedFiles = signedUrlsData?.data?.files || [];

            if (signedFiles.length !== mediaFiles.length) {
                console.error('Mismatch in signed URLs and media files count');
                return;
            }

            // Step 3: Upload each file and collect file metadata
            const uploadedFiles: any[] = [];

            for (let i = 0; i < mediaFiles.length; i++) {
                const file = mediaFiles[i].file;
                const { uploadUrl, fileKey } = signedFiles[i];

                if (!uploadUrl) {
                    console.error(`No upload URL for file ${i}`);
                    continue;
                }

                const uploadResponse = await uploadFileToSignedUrl(file, uploadUrl);

                if (uploadResponse.status === 200) {
                    uploadedFiles.push({
                        fileKey,
                        fileType: file.type,
                        featured: i === 0, // Optional: first image/video is featured
                    });
                } else {
                    console.error(`Upload failed for file ${i}`);
                }
            }

            if (uploadedFiles.length === 0) {
                console.error('No files were uploaded successfully');
                return;
            }

            // Step 4: Add uploaded file info to the form state
            setForm(prev => ({
                ...prev,
                files: uploadedFiles
            }));



            await request('/places', {
                method: 'POST',
                body: {
                    ...form,
                    files: uploadedFiles,
                },
                authorization: true,

            });
            alert('Place added successfully!');
            setForm(emptyForm);

            /**
             * STACK CLEAR REQUIRED HERE:
             * After successful place submission, we replace the input form page in history to:
             * 1. Prevent users from navigating back to the place form after successful submission
             * 2. Avoid accidental duplicate place creation if user hits back button
             * 3. Provide clean navigation flow to the all places listing page
             * 4. Clear potentially large image/media data from form history
             * 5. Follow standard content creation UX patterns
             */
            navigate('/allplaces', { replace: true });
        } catch (error) {
            console.error('Submission failed:', error);
            alert('Error submitting the form.');
        }




    };


    return (
        <>
        <TopNavBar title="Add Place" canGoBack={true} />
        <Paper sx={{ p: 3, mb: 30 }}>
            {/* Draft Management Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 3 }}>
                {/* <Typography variant="h5">Add New Place</Typography> */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={() => navigate('/place/drafts')}>
                        <DraftIcon />
                        <Typography fontWeight={"medium"} sx={{ color: '#008387', textTransform: 'none' }}>{'Open from Draft'}</Typography>
                    </Box>
                </Box>
            </Box>

            <form onSubmit={handleSubmit} onReset={() => setForm(emptyForm)}>
                <Stack spacing={3}>
                    <MapSelector
                        onLocationSelect={(location) => {
                            handleLocationSelect(location.lat, location.lng, location.address);
                        }}
                        initialLocation={currentLocation || undefined}
                        />


                    {/* Media Section */}
                    <Box sx={{ width: '100%', maxWidth: '800px', my: 2 }}>

                        <MediaUpload
                            value={imagesData}
                            onImagesChange={(images: any) => {
                                console.log('images outside', images);
                                setImagesData(images);
                                // Clear image validation error when images are added
                            }}
                            maxImages={5}
                            type={'image'}
                            mediaMode={'image-only'}
                            />
                    </Box>
                    <Grid container spacing={2}>
                        <Grid sx={{ width: '100%', maxWidth: '800px' }}>
                            <TextField
                                name="name"
                                label="Place Name"
                                value={form.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                />
                        </Grid>
                        <Grid sx={{ width: '100%', maxWidth: '800px' }}>
                            <TextField
                                name="description"
                                label="Description"
                                value={form.description}
                                onChange={handleChange}
                                multiline
                                rows={4}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                onBlur={handleDescriptionBlur}
                                />

                        </Grid>
                        <Grid sx={{ width: '100%', maxWidth: '800px' }}>
                            <TextField
                                name="address"
                                label="Address"
                                value={form.address}
                                onChange={handleChange}
                                fullWidth
                                required
                                sx={{ minWidth: '300px' }}
                                />
                        </Grid>
                    </Grid>

                    {/* Categories and Type Section */}
                    <Box>
                        <Grid container spacing={2}>
                            <Grid sx={{ width: '100%', maxWidth: '400px' }}>
                                {/* <CategoryChips
                                        categories={[...categories]}
                                        selectedCategories={form.categories}
                                        onChange={(newCategories) => setForm(prev => ({
                                            ...prev,
                                            categories: newCategories
                                        }))}
                                        maxInitialDisplay={4}
                                    /> */}
                                <CategoryChips
                                    categories={categories}
                                    selectedCategories={selectedCategories}
                                    onChange={(newCategories) => {
                                        setSelectedCategories(newCategories.map(category => category.name));
                                        setForm(prev => ({
                                            ...prev,
                                            categories: newCategories.map(category => category.name)
                                        }));
                                    }}
                                    maxInitialDisplay={6}
                                />
                            </Grid>
                            {/* <Grid sx={{ width: '100%', maxWidth: '400px' }}>
                                <Box sx={{ backgroundColor: 'white', p: 2, borderRadius: 1 }}>
                                    <Typography variant="subtitle1" sx={{ mb: 2 }}>Type*</Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {typeOptions.map((type) => (
                                            <Chip
                                                key={type}
                                                label={type}
                                                onClick={() => setForm(prev => ({ ...prev, type: type }))}
                                                variant={form.type === type ? "filled" : "outlined"}
                                                color={form.type === type ? "primary" : "default"}
                                                sx={{ mb: 1 }}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Grid> */}
                        </Grid>
                    </Box>

                    <Divider />

                    {/* Owner Details Section */}
                    <Box>

                        <Grid container spacing={2}>
                            <TextField
                                label="Email"
                                type="email"
                                value={form.owner_details.email}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    owner_details: { ...prev.owner_details, email: e.target.value }
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Owner Name"
                                value={form.owner_details.name}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    owner_details: { ...prev.owner_details, name: e.target.value }
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Phone"
                                value={form.owner_details.phone}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    owner_details: { ...prev.owner_details, phone: e.target.value }
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Whatsapp"
                                value={form.owner_details.whatsapp}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    owner_details: { ...prev.owner_details, whatsapp: e.target.value }
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                        </Grid>
                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                            <TextField
                                label="Website"
                                value={form.social.find(item => item.key === 'website')?.value}
                                // onChange={(e) => setForm(prev => ({
                                //     ...prev,
                                //     social: prev.social.map(item => item.key === 'website' ? { ...item, value: e.target.value } : item)
                                // }))}
                                onChange={(e: any) => {
                                    setForm(prev => ({
                                        ...prev,
                                        social: prev.social.map(item => item.key === 'website' ? { ...item, value: e.target.value } : item)
                                    }));
                                }}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Facebook"
                                value={form.social.find(item => item.key === 'facebook')?.value}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    social: prev.social.map(item => item.key === 'facebook' ? { ...item, value: e.target.value } : item)
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Instagram"
                                value={form.social.find(item => item.key === 'instagram')?.value}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    social: prev.social.map(item => item.key === 'instagram' ? { ...item, value: e.target.value } : item)
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />
                            <TextField
                                label="Youtube"
                                value={form.social.find(item => item.key === 'youtube')?.value}
                                onChange={(e) => setForm(prev => ({
                                    ...prev,
                                    social: prev.social.map(item => item.key === 'youtube' ? { ...item, value: e.target.value } : item)
                                }))}
                                fullWidth
                                sx={{ minWidth: '300px' }}
                            />

                        </Grid>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2 ,justifyContent: 'flex-end'}}>
                      
                        <Button
                            variant="outlined"
                            onClick={handleSaveDraft}
                            sx={{
                                borderColor: '#FF6B35',
                                color: '#FF6B35',
                                py: 1,
                                borderRadius: '25px',
                                textTransform: 'none',
                                fontSize: '16px',
                                fontWeight: 'bold',
                            }}
                        >
                            {isSavingDraft ? 'Saving Draft…' : 'Save as Draft'}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            type="submit"
                            size="large"
                            sx={{ backgroundColor: '#F57C00', color: 'white', borderRadius: '100px' }}
                        >
                            Submit
                        </Button>
                    </Box>
                </Stack>
            </form>

        </Paper>
        </>

    );
};

export default PlaceForm;
